import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import {
  Shift,
  StartedShift,
  OpenShiftsResponse,
  ShiftMonitorResponse,
  ShiftHistoryQuery,
  ShiftHistoryResponse,
  ShiftDetailResponse,
} from '../interfaces/shift-interfaces';

// Typed wrapper over the shift clock (lobbero_be/src/CRUD/shifts.js, mounted at
// app.use('/api', shiftsRoutes) — index.js:218 — so every path is /api/shifts/*).
//
// Uses the DEFAULT axios instance on purpose, exactly like packages-client and
// iq-client, so every call inherits the interceptors from axios-setup.ts:
// withCredentials for the httpOnly session cookies, the X-CSRF-Token
// double-submit header, and the single-flight 401 -> refresh -> retry. There is
// no auth code in this file and there must not be.
//
// NO IQ_TIMEOUT HERE. iq-client pins 75s because a Gemini call sits behind those
// routes and an aborted vendor call is still billed. These are four short SQL
// statements inside one RLS transaction; the default axios timeout is right, and
// borrowing 75s would leave a concierge staring at a dead button for over a
// minute when the API is down.

/** The 409 from POST /shifts/start (shifts.js:119-123).
 *
 *  A FORGOTTEN CLOCK-OUT BLOCKS THE NEXT CLOCK-IN at that building and is never
 *  auto-closed — not by a clock-in, not by a cron (shifts.js:11-17). That puts a
 *  blocking state between a concierge and the start of their shift, at 07:00,
 *  with somebody waiting at the desk. So the offending shift is part of the 409
 *  CONTRACT, not diagnostics: it travels on this error so the UI can render a
 *  resolve affordance and finish the job in one interaction instead of sending
 *  the desk to a support ticket. */
export class ShiftAlreadyOpenError extends Error {
  readonly openShift: Shift | null;
  constructor(message: string, openShift: Shift | null) {
    super(message);
    this.name = 'ShiftAlreadyOpenError';
    this.openShift = openShift;
  }
}

/** The 404 from POST /shifts/end (shifts.js:168-171). Its own class because it
 *  is a benign disagreement — the client thought a shift was open and it was
 *  not — and should read as "you are already clocked out", never as a failure. */
export class NoOpenShiftError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoOpenShiftError';
  }
}

interface ShiftErrorBody {
  message?: string;
  code?: string;
  open_shift?: Shift;
}

// Translation, on the asIqError pattern (iq-client.ts:49-71): the server's own
// sentence is kept when it sent one, because it is the more specific of the two
// ("ended_at cannot be in the future" beats any generic fallback). Only the
// wholly unstructured failures get a written-here sentence.
const asShiftError = (error: unknown, fallback: string): unknown => {
  if (!axios.isAxiosError(error)) return error;
  const status = error.response?.status;
  const data = error.response?.data as ShiftErrorBody | undefined;

  if (status === 409 && data?.code === 'SHIFT_ALREADY_OPEN') {
    return new ShiftAlreadyOpenError(
      data.message || 'There is already an open shift at this building.',
      data.open_shift || null
    );
  }
  if (status === 404 && data?.code === 'NO_OPEN_SHIFT') {
    return new NoOpenShiftError(
      data.message || 'There is no open shift at this building.'
    );
  }
  if (data?.message) return new Error(data.message);

  return new Error(fallback);
};

/** CLOCK IN — and, since backend migration 076, take over the desk's pending
 *  handover in the same transaction. `claimed_handoff` on the reply is the
 *  caller's cue to re-read the handover inbox; nothing else can tell it that a
 *  record it could not see a moment ago is now addressed to it. */
export const startShift = (buildingId: number): Promise<StartedShift> =>
  axios
    .post<StartedShift>(`${apiRoute}/shifts/start`, { building_id: buildingId })
    .then(r => r.data)
    .catch(e => {
      throw asShiftError(e, 'Could not clock in. Try again.');
    });

export const endShift = (buildingId: number): Promise<Shift> =>
  axios
    .post<Shift>(`${apiRoute}/shifts/end`, { building_id: buildingId })
    .then(r => r.data)
    .catch(e => {
      throw asShiftError(e, 'Could not clock out. Try again.');
    });

/** Returns the WHOLE list. Callers filter by building; see the note on
 *  OpenShiftsResponse for why this is not narrowed here. */
export const getOpenShifts = (): Promise<OpenShiftsResponse> =>
  axios
    .get<OpenShiftsResponse>(`${apiRoute}/shifts/open`)
    .then(r => r.data)
    .catch(e => {
      throw asShiftError(e, 'Could not read your open shifts.');
    });

/** Close a stale shift with an explicit end time.
 *
 *  `endedAt` must be a valid instant, not in the future, and after the shift
 *  started — all three are 400s from the server (shifts.js:231-238, :261-262)
 *  and all three carry a usable sentence, which is why they are surfaced as
 *  plain Errors rather than being pre-validated into silence here. Self-resolve
 *  is always allowed; resolving somebody else's shift needs `shift:manage` and
 *  403s otherwise (shifts.js:251-259). */
export const resolveShift = (
  shiftId: string,
  endedAt: string
): Promise<Shift> =>
  axios
    .post<Shift>(`${apiRoute}/shifts/${shiftId}/resolve`, { ended_at: endedAt })
    .then(r => r.data)
    .catch(e => {
      throw asShiftError(e, 'Could not close that shift.');
    });

/** Gated on `user:read`, so a concierge gets 403 rather than an empty list.
 *  Callers must check the permission before mounting the surface — a 403 the
 *  user cannot act on is not a state worth rendering. */
export const getShiftMonitor = (
  buildingId: number
): Promise<ShiftMonitorResponse> =>
  axios
    .get<ShiftMonitorResponse>(`${apiRoute}/shifts/monitor`, {
      params: { building_id: buildingId },
    })
    .then(r => r.data)
    .catch(e => {
      throw asShiftError(e, 'Could not read the shift monitor.');
    });

/* ─────────────────────────  THE HISTORY TAB  ─────────────────────────
 *
 * Both routes are gated on `user:read` — the same supervisory gate the monitor
 * carries — so a concierge gets 403, not an empty list. Callers must check the
 * permission before mounting the surface: a 403 the user cannot act on is not a
 * state worth rendering.
 *
 * NEITHER CALL BILLS ANYTHING. The detail view shows the handover that was
 * frozen when the shift ended plus the raw log window, and never asks the model
 * for anything — a browse surface behind a vendor call is how a month of
 * scrolling empties an IQ budget. So these get the ordinary axios timeout, not
 * iq-client's 75s.
 */

/** Every shift the caller can see, newest first.
 *
 *  FILTERS NARROW, THEY DO NOT AUTHORISE. Passing no filter is a legitimate
 *  question — "every desk I can see" — and is what the tab asks on first load.
 *  A building or portfolio outside the caller's reach comes back as an empty
 *  page rather than a 403, deliberately: a 403 would confirm the id exists. */
export const listShiftHistory = (
  query: ShiftHistoryQuery = {}
): Promise<ShiftHistoryResponse> =>
  axios
    .get<ShiftHistoryResponse>(`${apiRoute}/shifts/history`, {
      params: {
        // Omitted rather than sent empty. The server treats '' as absent too,
        // but a request that does not mention a filter is the honest wire
        // representation of "no filter".
        ...(query.buildingId != null ? { building_id: query.buildingId } : {}),
        ...(query.portfolioId != null && query.buildingId == null
          ? { portfolio_id: query.portfolioId }
          : {}),
        ...(query.userId ? { user_id: query.userId } : {}),
        ...(query.from ? { from: query.from } : {}),
        ...(query.to ? { to: query.to } : {}),
        ...(query.openOnly ? { open: 'true' } : {}),
        ...(query.limit != null ? { limit: query.limit } : {}),
        ...(query.offset ? { offset: query.offset } : {}),
      },
    })
    .then(r => r.data)
    .catch(e => {
      throw asShiftError(e, 'Could not read the shift history.');
    });

/** What happened on one shift: the frozen handover if there was one, plus the
 *  log window it covered.
 *
 *  A 404 covers a bad id, another organisation's shift and a building outside
 *  the caller's scope — none of them distinguishable to the caller by design. */
export const getShiftDetail = (
  shiftId: string
): Promise<ShiftDetailResponse> =>
  axios
    .get<ShiftDetailResponse>(`${apiRoute}/shifts/${shiftId}/detail`)
    .then(r => r.data)
    .catch(e => {
      throw asShiftError(e, 'Could not read that shift.');
    });
